/**
 * Who may use the back office, and what they get once inside.
 *
 * The roles themselves live only as a Firebase custom claim (`account_roles`,
 * comma-joined) — there is no role column anywhere in the database. This module is
 * the one place that parses that claim, because the gate used to be written out
 * twice, in `login.vue` and again in `firebaseAuth.ts`, and the second copy signed
 * a driver out on the next token refresh no matter what the first one decided.
 */
import type { Rule } from '@/plugins/casl/ability'

export type PortalRole = 'ADMIN' | 'DRIVER'

const PORTAL_ROLES: PortalRole[] = ['ADMIN', 'DRIVER']

/**
 * Splits the claim into roles.
 *
 * On `,` rather than by substring: the previous `roles.includes('ADMIN')` was a raw
 * substring test, so any future role whose name merely contained "ADMIN" would have
 * been admitted as one.
 */
export const parseAccountRoles = (claim: unknown): PortalRole[] => {
  if (typeof claim !== 'string' || claim.length === 0)
    return []

  const parsed = claim.split(',').map(role => role.trim())

  return PORTAL_ROLES.filter(role => parsed.includes(role))
}

/** Whether this account may open the back office at all. */
export const hasPortalRole = (claim: unknown): boolean =>
  parseAccountRoles(claim).length > 0

/**
 * CASL rules for a set of roles, as a union rather than a choice.
 *
 * Someone holding ADMIN and DRIVER is a real case — the claim is a list — and they get the
 * whole admin surface, which already contains everything a driver needs. No switcher:
 * asking an admin-driver which hat they are wearing would be a question with no useful
 * answer.
 *
 * Nothing has to be subtracted from an admin any more. That was only needed while a
 * driver-only page existed, which an admin without the role could reach and never fill.
 */
export const abilityRulesForRoles = (roles: PortalRole[]): Rule[] => {
  const rules: Rule[] = []

  if (roles.includes('ADMIN'))
    rules.push({ action: 'manage', subject: 'all' })

  // The orders list, scoped by the backend to what they are carrying, and the order behind
  // any of them. That is a driver's whole surface - the separate "My driving" page it used
  // to be was folded into the orders list's driving view, which does the same job from the
  // page they already had.
  if (roles.includes('DRIVER'))
    rules.push({ action: 'read', subject: 'Orders' })

  return rules
}

/**
 * The roles on the current session, read from the cookie login wrote.
 *
 * For screens both roles reach, where CASL answers "may you open this page" but not "is
 * this control yours to press". The authorization that matters is server-side either way -
 * this only decides whether to render a button that would be refused.
 */
export const currentPortalRoles = (): PortalRole[] => {
  const userData = useCookie<{ roles?: string[] } | null>('userData').value

  return PORTAL_ROLES.filter(role => (userData?.roles ?? []).includes(role))
}

export const isCurrentUserAdmin = () => currentPortalRoles().includes('ADMIN')

/**
 * Where a session lands after login.
 *
 * The same page for both roles now: a driver used to land on their own driving page, and
 * that page is gone - the orders list is scoped to what they are carrying and its driving
 * view does the same job. Kept as a function because the root redirect reads it and the
 * answer could differ again.
 */
export const landingRoute = (): string => 'apps-ecommerce-order-list'
